import { useState, useEffect, useCallback } from 'react';

const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true';

export default function useAuth() {
  const [user, setUser] = useState(USE_MOCK ? { uid: 'mock-user-1', email: 'kai@nexkoins.com' } : null);
  const [loading, setLoading] = useState(!USE_MOCK);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (USE_MOCK) return;

    let unsubscribe;
    async function init() {
      try {
        const { auth } = await import('../firebase/config');
        const { onAuthStateChanged } = await import('firebase/auth');
        unsubscribe = onAuthStateChanged(auth, (u) => {
          setUser(u);
          setLoading(false);
          // Ensure a users/{uid} profile doc exists so every person who logs in
          // shows up in Access → Portal Access / Tab Access (and can be granted
          // portals) without first having to open Settings. Merge = non-destructive.
          if (u) {
            (async () => {
              try {
                const { db } = await import('../firebase/config');
                const { doc, setDoc } = await import('firebase/firestore');
                await setDoc(doc(db, 'users', u.uid), {
                  email: (u.email || '').toLowerCase(),
                  lastLogin: Date.now(),
                }, { merge: true });
              } catch (e) {
                console.error('user profile upsert failed:', e);
              }
            })();
          }
        });
      } catch (err) {
        console.error('Firebase init failed:', err);
        setLoading(false);
      }
    }
    init();
    return () => unsubscribe && unsubscribe();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    if (USE_MOCK) {
      setUser({ uid: 'mock-user-1', email: 'kai@nexkoins.com' });
      return;
    }
    try {
      const { auth } = await import('../firebase/config');
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const signup = useCallback(async (email, password) => {
    setError(null);
    if (USE_MOCK) {
      setUser({ uid: 'mock-user-1', email: 'kai@nexkoins.com' });
      return;
    }
    try {
      const { auth } = await import('../firebase/config');
      const { createUserWithEmailAndPassword } = await import('firebase/auth');
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    if (USE_MOCK) {
      setUser(null);
      return;
    }
    const { auth } = await import('../firebase/config');
    const { signOut } = await import('firebase/auth');
    await signOut(auth);
  }, []);

  return { user, loading, error, login, signup, logout };
}
